import { ResolvedError } from '@wix/error-handler-types';
import type { usePanorama } from '@wix/yoshi-flow-editor';

type PanoramaErrorTags = Record<
  string,
  string | number | boolean | null | undefined
>;

// Ambassador errors append the response body to the message; HTML error pages
// make every occurrence unique, so drop them and keep the status line.
const HTML_RESPONSE_BODY = /\nRESPONSE:\s*"?(?:\\n|\s)*<[\s\S]*$/;

// Ambassador errors carry a top-level `httpStatus` (-1 when there was no response)
const getAmbassadorStatus = (e: unknown): number | undefined => {
  if (typeof e !== 'object' || e === null || !('httpStatus' in e)) {
    return undefined;
  }
  return typeof e.httpStatus === 'number' ? e.httpStatus : undefined;
};

/**
 * Errors thrown by the app's HTTP client are `ResolvedError` plain objects (from
 * `getResolvedError`), not `Error` instances — so `String(e)` degrades to
 * "[object Object]". This extracts the resolved message and known error metadata
 * so panorama logs stay readable and groupable.
 */
export const toPanoramaError = (
  e: unknown,
  tags: PanoramaErrorTags = {},
): { error: Error; tags: PanoramaErrorTags } => {
  const resolved = e as Partial<ResolvedError>;
  const rawError =
    e instanceof Error ? e : new Error(resolved?.message ?? String(e));
  const message = rawError.message.replace(HTML_RESPONSE_BODY, '');
  const error = message === rawError.message ? rawError : new Error(message);
  const ambassadorStatus = getAmbassadorStatus(e);

  return {
    error,
    tags: {
      ...tags,
      ...(resolved?.applicationError?.code
        ? { appErrorCode: resolved.applicationError.code }
        : {}),
      ...(resolved?.httpError?.response?.status
        ? { httpStatus: String(resolved.httpError.response.status) }
        : ambassadorStatus === undefined
        ? {}
        : { httpStatus: String(ambassadorStatus) }),
      ...(resolved?.requestId ? { requestId: resolved.requestId } : {}),
    },
  };
};

export const reportPanoramaError = (
  panorama: ReturnType<typeof usePanorama>,
  e: unknown,
  tags: PanoramaErrorTags,
) => {
  const { error, tags: errorTags } = toPanoramaError(e, tags);

  panorama.errorMonitor().reportError(error, { tags: errorTags });
};
